import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { useSession } from "../lib/session.js";
import { Link } from "../lib/router.js";
import { timeOfDayGreeting } from "../lib/format.js";
import { PageSpinner } from "../components/ui.js";
import { QuickLogModal, type QuickLogType } from "../components/QuickLogModal.js";
import { EveningCheckinModal } from "../components/EveningCheckinModal.js";
import { ProposalsCard } from "../components/ProposalsCard.js";
import { IconCheck, IconHeart, IconSparkles, IconTarget } from "../components/icons.js";

interface Task {
  id: string;
  timeBlock: string;
  taskType: string;
  title: string;
  detail: string | null;
  why: string | null;
  status: "pending" | "done" | "skipped" | "snoozed";
}
interface Rec { key: string; kind: string; tone: "positive" | "neutral" | "suggestion"; message: string }
interface GoalProg { goal: { id: string; title: string; status: string }; label: string; ratio: number | null; met: boolean }
interface Dashboard {
  nutrition: { hasPlan: boolean; calories: number; calorieTarget: number | null; proteinG: number; proteinTarget: number | null; waterOz: number; waterTarget: number | null; strategy: string | null };
  weight: { current: number | null; change: number | null; count: number; needsWeighIn: boolean };
  consistency: { activeToday: boolean; currentRun: number; bestWeek: number; daysThisMonth: number; last28: { date: string; active: boolean }[] };
  recommendations: Rec[];
  goals: GoalProg[];
  recentMilestone: { title: string; detail: string | null; achievedAt: string } | null;
  checkins: { morningDone: boolean; eveningDone: boolean };
  pendingProposals: number;
}
interface TodayData {
  date: string;
  phase: string | null;
  inRecovery: boolean;
  recovery: { daysSinceSurgery: number | null; surgeryName: string | null } | null;
  plan: { isMinimumDay: boolean; tasks: Task[] };
  totals: { waterOz: number; walkMinutes: number; steps: number; sleepHours: number | null; hadRecoveryCheckin: boolean };
  readiness: { energy: number | null } | null;
  dashboard: Dashboard;
  brief: { content: string; source: string } | null;
}

const BLOCK_ORDER = ["morning", "midday", "afternoon", "evening", "before_bed", "anytime"];
const BLOCK_LABELS: Record<string, string> = {
  morning: "Morning", midday: "Midday", afternoon: "Afternoon", evening: "Evening", before_bed: "Before bed", anytime: "Anytime"
};

function todayLocalDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function TodayPage() {
  const { user } = useSession();
  const [data, setData] = useState<TodayData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<QuickLogType | null>(null);
  const [evening, setEvening] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [refreshingBrief, setRefreshingBrief] = useState(false);
  const date = todayLocalDate();

  const load = useCallback(async () => {
    try {
      setData(await api.get<TodayData>(`/api/today?date=${date}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load today.");
    }
  }, [date]);

  useEffect(() => { void load(); }, [load]);

  async function setStatus(task: Task, status: Task["status"]) {
    setData((prev) =>
      prev ? { ...prev, plan: { ...prev.plan, tasks: prev.plan.tasks.map((t) => (t.id === task.id ? { ...t, status } : t)) } } : prev
    );
    await api.post(`/api/today/tasks/${task.id}/status`, { status }).catch(() => load());
  }

  async function minimumDay() {
    await api.post(`/api/today/minimum-day?date=${date}`).catch(() => undefined);
    await load();
  }

  async function refreshBrief() {
    setRefreshingBrief(true);
    try {
      const res = await api.post<{ brief: { content: string; source: string } }>(`/api/today/brief/refresh?date=${date}`);
      setData((prev) => (prev ? { ...prev, brief: res.brief } : prev));
    } catch { /* ignore */ } finally {
      setRefreshingBrief(false);
    }
  }

  if (!data) {
    return (
      <div>
        <div className="page-header"><h1>Today</h1></div>
        {error ? <div className="alert alert-danger">{error}</div> : <PageSpinner />}
      </div>
    );
  }

  const name = user?.displayName || user?.username || "there";
  const d = data.dashboard;
  const activeTasks = data.plan.tasks.filter((t) => t.status !== "snoozed");
  const snoozed = data.plan.tasks.filter((t) => t.status === "snoozed");
  const done = activeTasks.filter((t) => t.status === "done").length;
  const total = activeTasks.length;
  const byBlock = BLOCK_ORDER.map((block) => ({ block, tasks: activeTasks.filter((t) => t.timeBlock === block) })).filter((g) => g.tasks.length > 0);

  return (
    <div>
      <div className="page-header">
        <div className="greeting">
          {new Date(date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        </div>
        <h1>{timeOfDayGreeting()}, {name}</h1>
        <div className="row mt-2 wrap">
          {data.inRecovery ? (
            <span className="badge badge-accent">
              <IconHeart width={13} height={13} /> Recovery
              {data.recovery?.daysSinceSurgery != null && data.recovery.daysSinceSurgery >= 0 ? ` · Day ${data.recovery.daysSinceSurgery}` : ""}
            </span>
          ) : (
            data.phase && <span className="badge badge-muted">{data.phase}</span>
          )}
          {data.plan.isMinimumDay && <span className="badge badge-muted">Minimum day</span>}
          <span className="dim text-sm">{done}/{total} done</span>
        </div>
      </div>

      {data.inRecovery && (
        <div className="alert alert-info">
          Healing comes first. Walk as much as you comfortably can, stay hydrated, and keep your protein up — no strenuous exercise until you record clearance.
        </div>
      )}

      {/* Coach brief */}
      {data.brief && (
        <div className="card brief-card">
          <div className="brief-head">
            <span className="card-title"><IconSparkles width={16} height={16} /> Today's brief</span>
            <button className="btn btn-sm btn-ghost" onClick={refreshBrief} disabled={refreshingBrief}>{refreshingBrief ? "…" : "Refresh"}</button>
          </div>
          <p className="brief-text">{data.brief.content}</p>
          {data.brief.source !== "deterministic" && <p className="dim text-xs" style={{ margin: 0 }}>From your coach, grounded in your data.</p>}
        </div>
      )}

      {/* Suggestions / proposals */}
      {d.pendingProposals > 0 && <ProposalsCard onResolved={load} />}

      {/* Recommendations */}
      {d.recommendations.length > 0 && (
        <div className="stack" style={{ marginTop: "var(--space-4)" }}>
          {d.recommendations.map((r) => (
            <div key={r.key} className={`rec rec-${r.tone}`}>{r.message}</div>
          ))}
        </div>
      )}

      {/* Check-in prompts */}
      {(!d.checkins.morningDone || !d.checkins.eveningDone) && (
        <div className="row wrap mt-4" style={{ gap: "var(--space-3)" }}>
          {!d.checkins.morningDone && (
            <button className="checkin-prompt" onClick={() => setModal("readiness")}>
              <div style={{ fontWeight: 600 }}>Morning check-in</div>
              <div className="muted text-sm">How you feel shapes today.</div>
            </button>
          )}
          {!d.checkins.eveningDone && (
            <button className="checkin-prompt" onClick={() => setEvening(true)}>
              <div style={{ fontWeight: 600 }}>Evening check-in</div>
              <div className="muted text-sm">How did today go?</div>
            </button>
          )}
        </div>
      )}

      {/* The plan */}
      <div className="mt-6">
        <div className="row-between"><h2>Today's plan</h2></div>
        {byBlock.map((group) => (
          <div className="mt-4" key={group.block}>
            <div className="nav-section-label" style={{ padding: "0 0 var(--space-2)" }}>{BLOCK_LABELS[group.block]}</div>
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              {group.tasks.map((task) => (
                <div className="task-row" key={task.id}>
                  <button
                    className={`task-check${task.status === "done" ? " done" : ""}`}
                    onClick={() => setStatus(task, task.status === "done" ? "pending" : "done")}
                    aria-label={task.status === "done" ? "Mark not done" : "Mark done"}
                    type="button"
                  >
                    {task.status === "done" && <IconCheck width={16} height={16} />}
                  </button>
                  <div className="grow" style={{ minWidth: 0 }}>
                    <button
                      className="task-title"
                      onClick={() => setExpanded(expanded === task.id ? null : task.id)}
                      type="button"
                      style={{ textDecoration: task.status === "skipped" ? "line-through" : "none", opacity: task.status === "skipped" ? 0.6 : 1 }}
                    >
                      {task.title}
                    </button>
                    {task.detail && <div className="muted text-sm">{task.detail}</div>}
                    {expanded === task.id && (
                      <div className="why-box">
                        {task.why && <><strong>Why this?</strong> {task.why}</>}
                        <div className="row mt-2 wrap" style={{ gap: "var(--space-2)" }}>
                          {task.status !== "skipped" && <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "skipped")} type="button">Skip today</button>}
                          {task.status !== "snoozed" && <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "snoozed")} type="button">Later</button>}
                          {(task.status === "skipped" || task.status === "snoozed") && <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "pending")} type="button">Undo</button>}
                        </div>
                      </div>
                    )}
                  </div>
                  {task.status !== "skipped" ? (
                    <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "skipped")} type="button">Skip</button>
                  ) : (
                    <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "pending")} type="button">Undo</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {snoozed.length > 0 && (
          <div className="mt-3">
            <button className="btn btn-sm btn-ghost" onClick={() => setShowSnoozed((s) => !s)}>
              {showSnoozed ? "Hide" : "Show"} snoozed ({snoozed.length})
            </button>
            {showSnoozed && (
              <div className="card mt-2" style={{ padding: 0, overflow: "hidden" }}>
                {snoozed.map((task) => (
                  <div className="task-row" key={task.id}>
                    <div className="grow"><span className="muted">{task.title}</span></div>
                    <button className="btn btn-sm btn-ghost" onClick={() => setStatus(task, "pending")}>Bring back</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* At a glance */}
      <h2 className="mt-6">At a glance</h2>

      {/* Nutrition progress */}
      {d.nutrition.hasPlan && (
        <div className="card mt-3">
          <div className="card-header"><span className="card-title">Nutrition today</span><Link to="/nutrition" className="dim text-sm">Log →</Link></div>
          <ProgressRow label="Calories" value={d.nutrition.calories} target={d.nutrition.calorieTarget} unit="kcal" />
          <ProgressRow label="Protein" value={d.nutrition.proteinG} target={d.nutrition.proteinTarget} unit="g" />
          <ProgressRow label="Water" value={d.nutrition.waterOz} target={d.nutrition.waterTarget} unit="oz" />
        </div>
      )}

      {/* Quick tiles */}
      <div className="stat-grid mt-3">
        <GlanceTile label="Walking" value={`${data.totals.walkMinutes} min`} onClick={() => setModal("walk")} />
        <GlanceTile label="Water" value={`${Math.round(data.totals.waterOz)} oz`} onClick={() => setModal("water")} />
        <GlanceTile label="Sleep" value={data.totals.sleepHours != null ? `${data.totals.sleepHours} hr` : "—"} onClick={() => setModal("sleep")} />
        <GlanceTile
          label="Weight"
          value={d.weight.current != null ? `${d.weight.current} lb` : "—"}
          hint={d.weight.needsWeighIn ? "Due" : undefined}
          onClick={() => setModal("weight")}
        />
      </div>

      {/* Consistency */}
      <div className="card mt-3">
        <div className="card-header"><span className="card-title">Consistency</span></div>
        <div className="row wrap" style={{ gap: "var(--space-5)" }}>
          <Stat label="Recent run" value={`${d.consistency.currentRun}d`} />
          <Stat label="Best week" value={`${d.consistency.bestWeek}/7`} />
          <Stat label="This month" value={`${d.consistency.daysThisMonth}`} />
        </div>
        <div className="dot-grid mt-3" aria-hidden="true">
          {d.consistency.last28.map((day) => (
            <span key={day.date} className={`dot${day.active ? " on" : ""}`} title={day.date} />
          ))}
        </div>
        <p className="dim text-xs" style={{ marginTop: "var(--space-3)", marginBottom: 0 }}>One quiet day doesn't undo your progress — consistency is a rolling average, not a fragile streak.</p>
      </div>

      {/* Goals mini */}
      {d.goals.length > 0 && (
        <div className="card mt-3">
          <div className="card-header"><span className="card-title"><IconTarget width={16} height={16} /> Goals</span><Link to="/goals" className="dim text-sm">All →</Link></div>
          <div className="stack">
            {d.goals.map((g) => (
              <div key={g.goal.id}>
                <div className="row-between"><span className="text-sm">{g.goal.title}</span>{g.met && <span className="badge badge-success">Met</span>}</div>
                {g.ratio !== null && (
                  <div className="progress-track mt-1"><div className="progress-fill" style={{ width: `${Math.min(100, Math.round(g.ratio * 100))}%` }} /></div>
                )}
                <div className="dim text-xs" style={{ marginTop: 2 }}>{g.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent milestone */}
      {d.recentMilestone && (
        <div className="card mt-3 milestone-card">
          <span className="log-tile-icon" style={{ width: 36, height: 36, flexShrink: 0 }}>★</span>
          <div>
            <div style={{ fontWeight: 600 }}>{d.recentMilestone.title}</div>
            {d.recentMilestone.detail && <div className="muted text-sm">{d.recentMilestone.detail}</div>}
          </div>
        </div>
      )}

      {/* Minimum day */}
      {!data.plan.isMinimumDay && (
        <div className="card mt-6 center">
          <p className="muted text-sm">Rough day? Shrink today to the essentials — it still counts.</p>
          <button className="btn mt-4" onClick={minimumDay}>I need a minimum day</button>
        </div>
      )}

      {modal && <QuickLogModal type={modal} onClose={() => setModal(null)} onLogged={() => { void load(); }} />}
      {evening && <EveningCheckinModal onClose={() => setEvening(false)} onSaved={() => { setEvening(false); void load(); }} />}
    </div>
  );
}

function ProgressRow({ label, value, target, unit }: { label: string; value: number; target: number | null; unit: string }) {
  const pct = target ? Math.min(100, Math.round((value / target) * 100)) : null;
  const over = target != null && value > target * 1.05;
  return (
    <div style={{ marginBottom: "var(--space-3)" }}>
      <div className="row-between text-sm"><span>{label}</span><span className="dim">{value}{target != null ? ` / ${target}` : ""} {unit}</span></div>
      {pct !== null && (
        <div className="progress-track mt-1"><div className={`progress-fill${over ? " over" : ""}`} style={{ width: `${pct}%` }} /></div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div><div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{value}</div><div className="stat-label">{label}</div></div>
  );
}

function GlanceTile({ label, value, hint, onClick }: { label: string; value: string; hint?: string; onClick?: () => void }) {
  const content = (
    <>
      <div className="stat-label">{label}{hint && <span className="badge badge-accent" style={{ marginLeft: 6 }}>{hint}</span>}</div>
      <div className="stat-value" style={{ fontSize: "var(--text-xl)" }}>{value}</div>
    </>
  );
  if (onClick) {
    return <button className="stat" style={{ cursor: "pointer", textAlign: "left" }} onClick={onClick} type="button">{content}</button>;
  }
  return <div className="stat">{content}</div>;
}
